import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Plus, Unlink } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  useProductArticles,
  useRemoveProductArticle,
} from "../../../api/productsApi"
import type { ProductArticleRow } from "../../../api/types/masterData"
import { formatQuantity } from "../../../utils/format"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"
import { LinkArticleToProductDialog } from "./LinkArticleToProductDialog"

export function ProductArticlesSection() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { mutate: removeLink, isPending: removing } = useRemoveProductArticle()

  const { data, hasNextPage, fetchNextPage, isLoading } = useProductArticles(
    id,
    { limit: 20 },
  )

  const articles = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  if (!id) return null

  const columns: SubTableColumn<ProductArticleRow>[] = [
    {
      key: "position",
      header: "#",
      cell: (a) =>
        a.positionNumber != null ? (
          <span className="font-mono">{a.positionNumber}</span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-10",
      hideBelow: "280px",
    },
    {
      key: "articleNumber",
      header: translate("MC_MD_PANEL_COL_ARTICLE"),
      cell: (a) => <span className="font-mono">{a.articleNumber}</span>,
      className: "w-60",
    },
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_DESCRIPTION"),
      cell: (a) => <span className="block max-w-xs truncate">{a.name}</span>,
    },
    {
      key: "quantity",
      header: translate("MC_MD_PANEL_COL_QTY"),
      cell: (a) =>
        a.quantity != null ? (
          <span className="font-mono">
            {formatQuantity(a.quantity, a.unit, currentLanguage)}
          </span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-20",
      hideBelow: "360px",
    },
    {
      key: "restricted",
      header: translate("MC_MD_PANEL_COL_RESTRICTED"),
      cell: (a) =>
        a.restrictedSubstancesCount > 0 ? (
          <McPill tone="attention">{a.restrictedSubstancesCount}</McPill>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-24",
      hideBelow: "480px",
    },
    {
      key: "unlink",
      header: "",
      cell: (a) => (
        <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-grey5 hover:text-white"
            aria-label={translate("MC_MD_PANEL_ARTICLES_UNLINK")}
            disabled={removing}
            onClick={(e) => {
              e.stopPropagation()
              removeLink(
                { productId: id, articleId: a.id },
                {
                  onError: (err) =>
                    toastApiError(err, translate, "MC_MD_SAVE_ERROR"),
                },
              )
            }}
          >
            <Unlink size={14} />
          </Button>
        </PermissionGate>
      ),
      className: "w-10 text-right",
    },
  ]

  const addButton = (
    <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <Plus />
        {translate("MC_MD_PANEL_ARTICLES_LINK")}
      </Button>
    </PermissionGate>
  )

  return (
    <>
      {!isLoading && articles.length === 0 && (
        <p className="px-4 pt-3 text-[12px] text-grey5">
          {translate("MC_MD_PANEL_ARTICLES_EMPTY_HINT")}
        </p>
      )}
      <EntitySubTable
        title={translate("MC_MD_PANEL_PRODUCT_ARTICLES_TITLE")}
        items={articles}
        columns={columns}
        keyOf={(a) => a.id}
        hasMore={!!hasNextPage}
        onFetchMore={fetchNextPage}
        isLoading={isLoading}
        total={total}
        onRowClick={(a) => navigate(`../articles?id=${a.id}&source=nav`)}
        action={addButton}
      />
      <LinkArticleToProductDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        productId={id}
      />
    </>
  )
}
